import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { studioEnvironment, contactShadow, applyEnv } from './env.js';
import { buildPhone } from './generators/phone.js';
import { loadGLB } from './glb.js';
import { Analytics } from './analytics.js';
import { answer as assistantAnswer } from './assistant.js';
import { launchAR, isAndroid } from './ar.js';
import { CSS } from './styles.js';

export const GENERATORS = { phone: buildPhone };

const DEFAULTS = {
  version: 1,
  render: { exposure: 1.25, toneMapping: 'neutral', shadow: true, pixelRatio: 2 },
  camera: { fov: 30, home: [-2.3, 0.45, -3.4], minDistance: 1.5, maxDistance: 6.5, autoRotate: true, autoRotateSpeed: 1.2 },
  views: { front: [0, .1, 4.2], back: [0, .2, -4.2], side: [4.2, .1, 0] },
  ui: { toolbar: true, hotspots: true, hint: 'Glissez pour tourner · Touchez les points pour en savoir plus', assistant: false, ar: true, tour: true, compare: true },
  assistant: {}, analytics: {}, kiosk: { idleSeconds: 45 },
  products: []
};
const merge = (a, b) => { const o = { ...a }; for (const k in b) o[k] = b[k] && typeof b[k] === 'object' && !Array.isArray(b[k]) ? merge(a[k] || {}, b[k]) : b[k]; return o; };
const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const HILIGHT = [0x4285f4, 0xea4335, 0xfbbc04, 0x34a853].map(c => new THREE.Color(c));
const _v = new THREE.Vector3(), _n = new THREE.Vector3(), _q = new THREE.Quaternion(), _c = new THREE.Vector3();

export class XPViewer extends HTMLElement {
  static get observedAttributes() { return ['config', 'product', 'variant', 'mode', 'project', 'pb']; }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `<style>${CSS}</style>
      <div class="stage" part="stage">
        <div class="layer hs-layer"></div><div class="labels"></div>
        <div class="card" part="card"><div class="cap"></div><h4></h4><p></p></div>
        <div class="tour" hidden></div>
        <div class="tb" part="toolbar" hidden>
          <button data-a="left" data-tip="Tourner" aria-label="Tourner à gauche">‹</button><button data-a="right" aria-label="Tourner à droite">›</button>
          <button data-a="up" aria-label="Incliner vers le haut">˄</button><button data-a="down" aria-label="Incliner vers le bas">˅</button><hr>
          <button data-a="in" aria-label="Zoomer">+</button><button data-a="out" aria-label="Dézoomer">−</button><hr>
          <button data-a="fold" data-tip="Plier / déplier" aria-label="Plier / déplier" hidden>⟷</button>
          <button data-a="tour" data-tip="Visite guidée" aria-label="Visite guidée" hidden>▶</button>
          <button data-a="compare" data-tip="Comparer les tailles" aria-label="Comparer" hidden>⇆</button>
          <button data-a="ar" data-tip="Voir chez moi (AR)" aria-label="Réalité augmentée" hidden>◎</button>
          <button data-a="reset" data-tip="Recentrer" aria-label="Recentrer">↺</button>
          <button data-a="full" data-tip="Plein écran" aria-label="Plein écran">⤢</button>
        </div>
        <div class="ask" hidden><form><input placeholder="Posez une question sur ce produit…" aria-label="Question"><button>Demander</button></form></div>
        <div class="hint"></div>
        <div class="toast"></div>
        <div class="loading">Chargement…<i><b></b></i></div>
      </div>`;
    const $ = s => this.shadowRoot.querySelector(s);
    this.ui = { stage: $('.stage'), hs: $('.hs-layer'), labels: $('.labels'), card: $('.card'), tb: $('.tb'), hint: $('.hint'), toast: $('.toast'), loading: $('.loading'), bar: $('.loading b'), ask: $('.ask'), tour: $('.tour') };
    this.models = new Map();      // cache des modèles chargés
    this.hotspots = []; this.colorJobs = []; this.effects = { hilight: false, charge: false };
    this.foldT = 1; this.foldTarget = 1; this.tw = null; this.running = false; this.compareSet = null;
  }

  /* ================= cycle de vie ================= */
  connectedCallback() {
    this._io = new IntersectionObserver(([e]) => { this._visible = e.isIntersecting; this._visible ? this.resume() : this.pause(); });
    this._io.observe(this);
  }
  disconnectedCallback() { this.pause(); this._io?.disconnect(); this._ro?.disconnect(); this.analytics?.destroy(); }
  attributeChangedCallback(name, _, val) {
    if (name === 'config' && val) fetch(val).then(r => r.json()).then(c => { c.__base = new URL(val, location.href).href; this.config = c; }).catch(e => this._error(e));
    if ((name === 'project' || name === 'pb') && this.getAttribute('project')) { clearTimeout(this._pbT); this._pbT = setTimeout(() => this._loadPB()); }
    if (name === 'product' && this.cfg && val && val !== this.product?.id) this.setProduct(val, +(this.getAttribute('variant') || 0));
    if (name === 'variant' && this.product && val != null && +val !== this.variantIndex) this.setVariant(+val);
    if (name === 'mode' && val === 'kiosk') this._kiosk();
  }

  /** Charge la configuration publiée d'un projet depuis PocketBase (collection xp_projects). */
  _loadPB() {
    const pb = (this.getAttribute('pb') || 'https://pb.withmoonlabs.com').replace(/\/$/, ''), slug = this.getAttribute('project').replace(/[^a-z0-9-]/g, '');
    const url = `${pb}/api/collections/xp_projects/records?perPage=1&fields=published_config&filter=${encodeURIComponent(`slug='${slug}' && status='published'`)}`;
    fetch(url).then(r => r.json()).then(j => {
      const c = j.items?.[0]?.published_config;
      if (!c) throw new Error('Projet non publié : ' + slug);
      c.__base = pb + '/'; this.config = c;
    }).catch(e => this._error(e));
  }

  set config(c) {
    if (this.models.size) { if (this.model) this.scene.remove(this.model.root); this._exitCompare(true); this.models.clear(); this.model = null; this.product = null; }
    this.cfg = merge(DEFAULTS, c || {});
    this.cfg.__base = c?.__base || location.href;
    this._init3d();
    this.analytics?.destroy(); this.analytics = new Analytics(this, this.cfg.analytics);
    const ui = this.cfg.ui;
    this.ui.tb.hidden = !ui.toolbar;
    this.ui.hint.textContent = ui.hint || ''; this.ui.hint.hidden = !ui.hint;
    this.ui.ask.hidden = !ui.assistant; this.toggleAttribute('ui-assistant', !!ui.assistant);
    if (this.cfg.assistant?.placeholder) this.ui.ask.querySelector('input').placeholder = this.cfg.assistant.placeholder;
    this.showHotspots(ui.hotspots !== false);
    const first = this.getAttribute('product') || this.cfg.products[0]?.id;
    if (first) this.setProduct(first, +(this.getAttribute('variant') || 0));
    if (this.getAttribute('mode') === 'kiosk' || this.cfg.kiosk?.enabled) this._kiosk();
  }
  get config() { return this.cfg; }

  _init3d() {
    if (this.renderer) return this._applyRender();
    const r = this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    this.ui.stage.prepend(r.domElement);
    this.scene = new THREE.Scene();
    this.env = studioEnvironment(r);
    const key = new THREE.DirectionalLight(0xffffff, .9); key.position.set(2, 4, 3); this.scene.add(key);
    this.camera = new THREE.PerspectiveCamera(30, 1, .01, 80);
    this.controls = new OrbitControls(this.camera, r.domElement);
    Object.assign(this.controls, { enableDamping: true, dampingFactor: .08, enablePan: false, minPolarAngle: .2, maxPolarAngle: Math.PI - .2 });
    this.controls.addEventListener('start', () => { this.tw = null; this._userInteract(); });
    this.shadow = contactShadow(); this.scene.add(this.shadow);
    this._applyRender();
    this._ro = new ResizeObserver(() => this._resize()); this._ro.observe(this);
    this.clock = new THREE.Clock();
    this._bindUI();
    this.resume();
  }
  _applyRender() {
    const R = this.cfg.render, C = this.cfg.camera, r = this.renderer;
    r.setPixelRatio(Math.min(devicePixelRatio, R.pixelRatio || 2));
    r.toneMapping = R.toneMapping === 'aces' ? THREE.ACESFilmicToneMapping : R.toneMapping === 'agx' ? THREE.AgXToneMapping : (THREE.NeutralToneMapping ?? THREE.ACESFilmicToneMapping);
    r.toneMappingExposure = R.exposure;
    this.shadow.visible = R.shadow !== false;
    this.camera.fov = C.fov; this.camera.updateProjectionMatrix();
    Object.assign(this.controls, { minDistance: C.minDistance, maxDistance: C.maxDistance, autoRotate: !!C.autoRotate, autoRotateSpeed: C.autoRotateSpeed });
    if (this.cfg.theme?.accent) this.style.setProperty('--xp-accent', this.cfg.theme.accent);
    if (this.cfg.theme?.background) this.style.setProperty('--xp-bg', this.cfg.theme.background);
  }
  _resize() { const w = this.clientWidth, h = this.clientHeight; if (!w || !h) return; this.renderer.setSize(w, h, false); this.camera.aspect = w / h; this.camera.updateProjectionMatrix(); }
  pause() { this.running = false; }
  resume() { if (this.running || !this.renderer) return; this.running = true; this.clock.getDelta(); this._loop(); }
  _error(e) { console.error('[xp-viewer]', e); this.ui.loading.textContent = 'Impossible de charger le modèle 3D.'; this.ui.loading.hidden = false; }
  _emit(name, detail) { this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true })); }
  _toast(msg) { const t = this.ui.toast; t.textContent = msg; t.classList.add('on'); clearTimeout(this._tt); this._tt = setTimeout(() => t.classList.remove('on'), 2600); }

  /* ================= modèles ================= */
  async _getModel(p) {
    if (this.models.has(p.id)) return this.models.get(p.id);
    const m = p.model || {};
    let model;
    this.ui.loading.hidden = false; this.ui.bar.style.width = '10%';
    if (m.type === 'glb') {
      const src = new URL(m.src, this.cfg.__base).href;
      model = await loadGLB({ ...m, src }, this.renderer, f => (this.ui.bar.style.width = (10 + f * 90) + '%'));
    } else {
      const gen = GENERATORS[m.generator || 'phone'];
      model = gen(m.params || {});
    }
    applyEnv(model.root, this.env);
    for (const k in model.roles) model.roles[k] = [].concat(model.roles[k] || []);
    this.models.set(p.id, model);
    this.ui.loading.hidden = true;
    return model;
  }

  /** Affiche un produit (id de la config) avec le coloris n° variant. */
  async setProduct(id, variant = 0) {
    const p = this.cfg.products.find(x => x.id === id); if (!p) return;
    if (this.product?.id === id && this.model && !this.compareSet) return this.setVariant(variant);
    this._exitCompare(true); this.stopTour(); this._hideCard();
    const token = (this._token = Symbol());
    let model; try { model = await this._getModel(p); } catch (e) { return this._error(e); }
    if (token !== this._token) return;
    if (this.model) this.scene.remove(this.model.root);
    this.product = p; this.model = model; this.variantIndex = -1;
    model.root.position.set(0, 0, 0);
    this.scene.add(model.root);
    this.foldT = this.foldTarget = p.fold ?? 1; model.setFold(this.foldT);
    // Cadrage selon la taille du produit (référence : un téléphone de ~15 cm = 1,53 unité)
    const C = this.cfg.camera;
    this._fit = p.camera?.home ? 1 : Math.min(20, Math.max(.3, Math.max(model.size.H, model.size.W * .75) / 1.53));
    this.controls.minDistance = C.minDistance * this._fit; this.controls.maxDistance = C.maxDistance * this._fit;
    this._placeShadow();
    this._buildHotspots();
    await this.setVariant(variant, true);
    this.view('home', true);
    this.controls.autoRotate = !!this.cfg.camera.autoRotate;
    const tb = this.ui.tb;
    tb.querySelector('[data-a=fold]').hidden = !model.canFold;
    tb.querySelector('[data-a=tour]').hidden = !(this.cfg.ui.tour && this._tourSteps().length);
    tb.querySelector('[data-a=compare]').hidden = !(this.cfg.ui.compare && this.cfg.products.length > 1);
    tb.querySelector('[data-a=ar]').hidden = !this.cfg.ui.ar || (isAndroid() && !(p.ar && p.ar.glb)); // Android : Scene Viewer exige un fichier GLB
    this.setAttribute('product', id);
    this._emit('xp-product', { id, product: p });
    this.analytics.track('product', { product: id });
    if (!this._readyFired) { this._readyFired = true; this._emit('xp-ready', { viewer: this }); }
  }

  /** Change de coloris (transition douce). */
  async setVariant(i, instant = false) {
    const p = this.product, v = p?.variants?.[i]; if (!v) return;
    this.variantIndex = i;
    if (v.gltfVariant && this.model.selectVariant) await this.model.selectVariant(v.gltfVariant);
    const mats = v.materials || {};
    const targets = { ...mats };
    if (!targets.logo && mats.back?.color && this.model.roles.logo?.length) {
      const b = new THREE.Color(mats.back.color), dark = b.getHSL({}).l < .25;
      targets.logo = { color: '#' + b.clone().lerp(new THREE.Color(dark ? '#9a9da0' : '#ffffff'), .55).getHexString() };
    }
    this.colorJobs = [];
    for (const [role, props] of Object.entries(targets)) {
      for (const m of this.model.roles[role] || []) {
        for (const [k, val] of Object.entries(props)) {
          if (k === 'color' || k === 'sheenColor' || k === 'emissive') {
            const c = new THREE.Color(val);
            if (instant || !m[k]) { m[k]?.copy?.(c); } else this.colorJobs.push({ m, k, c });
            if (k === 'color' && m.sheenColor) m.sheenColor.copy(c).lerp(new THREE.Color('#fff'), .5);
          } else if (k in m) { m[k] = val; m.needsUpdate = true; }
        }
      }
    }
    this.setAttribute('variant', i);
    this._emit('xp-variant', { product: p.id, index: i, variant: v });
    if (!instant) this.analytics.track('variant', { product: p.id, variant: v.name });
  }

  _placeShadow() {
    const s = this.model.size; this.shadow.position.y = -s.H / 2 - .04;
    this.shadow.scale.set(Math.max(s.W * 2.2, 1.2), .9, 1);
  }

  /* ================= points d'intérêt ================= */
  _buildHotspots() {
    this.ui.hs.innerHTML = '';
    this.hotspots = (this.product.hotspots || []).map((h, i) => {
      let obj = this.model.anchors.get(h.anchor || h.id);
      if (!obj && h.position) {
        obj = new THREE.Object3D(); obj.position.set(...h.position);
        obj.userData.n = new THREE.Vector3(...(h.normal || [0, 0, 1])).normalize();
        this.model.root.add(obj); this.model.anchors.set(h.id, obj);
      }
      if (!obj) return null;
      const el = document.createElement('button'); el.className = 'hs'; el.type = 'button'; el.textContent = h.label || i + 1;
      el.setAttribute('aria-label', h.title || h.id); el.part = 'hotspot';
      el.onclick = e => { e.stopPropagation(); this.focus(h.id); };
      this.ui.hs.appendChild(el);
      return { def: h, obj, el };
    }).filter(Boolean);
  }
  showHotspots(on = true) { this._showHS = on; if (!on) this._hideCard(); }

  /** Centre la caméra sur un point d'intérêt et lance sa démo. */
  focus(id, { caption, quiet } = {}) {
    const h = this.hotspots.find(x => x.def.id === id); if (!h) return;
    this.setAutoRotate(false);
    h.obj.getWorldPosition(_v); h.obj.getWorldQuaternion(_q); _n.copy(h.obj.userData.n).applyQuaternion(_q);
    const k = this._fit || 1, dist = h.def.distance ?? 2.1 * k;
    const pos = _v.clone().add(_n.clone().multiplyScalar(dist)).add(new THREE.Vector3(.25 * k, .18 * k, 0).applyQuaternion(_q));
    this._tween(pos, _v.clone().multiplyScalar(.55));
    this.hotspots.forEach(x => x.el.classList.toggle('on', x === h));
    this._showCard(caption || '', h.def.title, h.def.text);
    this._runDemo(h.def.demo);
    this.activeHotspot = id;
    this._emit('xp-hotspot', { product: this.product.id, id, hotspot: h.def });
    if (!quiet) this.analytics.track('hotspot', { product: this.product.id, hotspot: id });
  }
  _showCard(cap, title, text) {
    const c = this.ui.card; c.querySelector('.cap').textContent = cap || ''; c.querySelector('.cap').hidden = !cap;
    c.querySelector('h4').textContent = title || ''; c.querySelector('p').textContent = text || ''; c.classList.add('on');
  }
  _hideCard() {
    this.ui.card.classList.remove('on'); this.activeHotspot = null;
    this.hotspots.forEach(x => x.el.classList.remove('on'));
    if (this._demoActive) this._resetDemo();
  }

  /* ================= démos & effets ================= */
  _runDemo(d) {
    this._resetDemo();
    if (!d) return;
    this._demoActive = true;
    if (d.screen && this.model.screen) this.model.screen.set(d.screen, d.screenParams || {});
    if (d.effect) this.setEffect(d.effect, true, true);
    if (d.fold != null) this.setFold(d.fold);
  }
  _resetDemo() {
    this._demoActive = false;
    (this.model?.screens || [this.model?.screen]).forEach(s => s && s.mode !== 'lock' && s.set('lock'));
    for (const k in this.effects) if (this.effects[k] === 'demo') this.effects[k] = false;
  }
  /** Effets : 'hilight' (LED colorées), 'charge' (recharge Pixelsnap animée). */
  setEffect(name, on = true, fromDemo = false) {
    this.effects[name] = on ? (fromDemo ? 'demo' : true) : false;
    if (name === 'charge' && this.model?.screen) this.model.screen.set(on ? 'charge' : 'lock', on ? { from: 18 } : {});
    if (name === 'hilight' && on && this.model?.screen && fromDemo) this.model.screen.set('gemini');
    this._emit('xp-effect', { name, on });
  }
  setFold(t) { this.foldTarget = Math.max(0, Math.min(1, t)); }
  setAutoRotate(on) { this.controls.autoRotate = on; this._emit('xp-autorotate', { on }); }

  /* ================= caméra ================= */
  _tween(pos, target = new THREE.Vector3(), dur = 900) { this.tw = { p0: this.camera.position.clone(), p1: pos, t0: this.controls.target.clone(), t1: target, s: performance.now(), dur }; }
  view(name = 'home', instant = false) {
    const p = name === 'home' ? (this.product?.camera?.home || this.cfg.camera.home) : (this.product?.views?.[name] || this.cfg.views[name]);
    if (!p) return;
    if (name !== 'home') this.setAutoRotate(false);
    const v = new THREE.Vector3(...p);
    if (name === 'home' && !this.product?.camera?.home) v.multiplyScalar(this._fit || 1);
    else if (name !== 'home') v.multiplyScalar(this._fit || 1);
    if (instant) { this.camera.position.copy(v); this.controls.target.set(0, 0, 0); this.tw = null; } else this._tween(v);
    this._hideCard();
  }
  orbit(dAz, dPol) {
    this.setAutoRotate(false);
    const off = this.camera.position.clone().sub(this.controls.target), s = new THREE.Spherical().setFromVector3(off);
    s.theta += dAz; s.phi = THREE.MathUtils.clamp(s.phi + dPol, .25, Math.PI - .25);
    this._tween(this.controls.target.clone().add(new THREE.Vector3().setFromSpherical(s)), this.controls.target.clone(), 600);
  }
  zoom(f) {
    const off = this.camera.position.clone().sub(this.controls.target);
    const L = THREE.MathUtils.clamp(off.length() * f, this.controls.minDistance, this.controls.maxDistance * (this.compareSet ? 2 : 1));
    this._tween(this.controls.target.clone().add(off.setLength(L)), this.controls.target.clone(), 450);
  }

  /* ================= visite guidée ================= */
  _tourSteps() { return (this.product?.tour || this.cfg.tour || []).filter(s => !s.hotspot || this.hotspots.some(h => h.def.id === s.hotspot)); }
  async playTour({ loop = false } = {}) {
    const steps = this._tourSteps(); if (!steps.length) return;
    this.stopTour(); this._exitCompare();
    const id = (this._tourId = Symbol());
    this.ui.tour.hidden = false; this.ui.tour.innerHTML = steps.map(() => '<span><i></i></span>').join('');
    this.ui.tb.querySelector('[data-a=tour]').classList.add('on');
    this._emit('xp-tour', { state: 'start' }); this.analytics.track('tour_start', { product: this.product.id });
    do {
      for (let i = 0; i < steps.length; i++) {
        if (id !== this._tourId) return;
        const s = steps[i], dur = s.duration || 4500;
        if (s.hotspot) this.focus(s.hotspot, { caption: s.caption || `Étape ${i + 1}/${steps.length}`, quiet: true });
        else { if (s.view) this.view(s.view); this._showCard(s.caption || `Étape ${i + 1}/${steps.length}`, s.title, s.text); this._runDemo(s.demo); }
        if (s.title && s.hotspot) this._showCard(s.caption || `Étape ${i + 1}/${steps.length}`, s.title, s.text);
        if (s.demo && s.hotspot) this._runDemo(s.demo);
        const bar = this.ui.tour.children[i]?.firstChild; const t0 = performance.now();
        await new Promise(res => { const f = () => { if (id !== this._tourId) return res(); const k = (performance.now() - t0) / dur; if (bar) bar.style.width = Math.min(100, k * 100) + '%'; k >= 1 ? res() : requestAnimationFrame(f); }; f(); });
      }
      [...this.ui.tour.children].forEach(s => (s.firstChild.style.width = '0'));
    } while (loop && id === this._tourId);
    if (id === this._tourId) { this.stopTour(); this.view('home'); this.setAutoRotate(true); }
  }
  stopTour() {
    if (!this._tourId) return;
    this._tourId = null; this.ui.tour.hidden = true;
    this.ui.tb.querySelector('[data-a=tour]').classList.remove('on');
    this._hideCard(); this._emit('xp-tour', { state: 'stop' });
  }

  /* ================= comparateur ================= */
  /** Affiche plusieurs produits côte à côte, à l'échelle réelle. */
  async compare(ids) {
    ids = (ids || this.cfg.compareSet || this.cfg.products.map(p => p.id)).slice(0, 4);
    this.stopTour(); this._hideCard();
    const list = [];
    for (const id of ids) { const p = this.cfg.products.find(x => x.id === id); if (p) list.push({ p, m: await this._getModel(p) }); }
    if (this.model) this.scene.remove(this.model.root);
    this.compareSet = list;
    list.forEach(({ p, m }) => { m.setFold(0); m._compareVariant = 0; this.scene.add(m.root); });
    const gap = .35; let total = list.reduce((a, { m }) => a + m.size.W, 0) + gap * (list.length - 1), x = -total / 2;
    const maxH = Math.max(...list.map(({ m }) => m.size.H));
    list.forEach(({ m }) => { m.root.position.set(x + m.size.W / 2, (m.size.H - maxH) / 2, 0); m.root.rotation.y = 0; x += m.size.W + gap; });
    this.shadow.position.y = -maxH / 2 - .04; this.shadow.scale.set(total * 1.6, 1.2, 1);
    this.ui.labels.innerHTML = list.map(({ p }) => `<div class="label">${p.name}<small>${p.compareLabel || ''}</small></div>`).join('');
    const dist = Math.max(total, maxH * 1.4) / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2))) * 1.15 / Math.min(1.6, this.camera.aspect);
    this.setAutoRotate(false);
    this._tween(new THREE.Vector3(0, .1, Math.max(4, dist)));
    this.ui.tb.querySelector('[data-a=compare]').classList.add('on');
    this.ui.hs.style.display = 'none';
    this._emit('xp-compare', { ids }); this.analytics.track('compare', { ids: ids.join(',') });
  }
  _exitCompare(silent) {
    if (!this.compareSet) return;
    this.compareSet.forEach(({ m, p }) => { this.scene.remove(m.root); m.root.position.set(0, 0, 0); m.setFold(p.fold ?? 1); });
    this.compareSet = null; this.ui.labels.innerHTML = ''; this.ui.hs.style.display = '';
    this.ui.tb.querySelector('[data-a=compare]').classList.remove('on');
    if (!silent && this.product) { const id = this.product.id; this.models.get(id) && (this.product = null, this.setProduct(id, Math.max(0, this.variantIndex))); }
  }

  /* ================= assistant ================= */
  /** Pose une question : renvoie { answer, hotspot } et montre la zone concernée. */
  async ask(question) {
    if (!this.product || !question?.trim()) return null;
    this._exitCompare(); this.stopTour();
    const res = await assistantAnswer(question, this.product, this.cfg.assistant);
    if (res.hotspot) { this.focus(res.hotspot.id, { caption: 'Réponse', quiet: true }); this._showCard('Réponse', res.hotspot.title, res.hotspot.text); }
    else this._showCard('Réponse', '', res.answer);
    this.analytics.track('ask', { product: this.product.id, question: question.slice(0, 200), hotspot: res.hotspot?.id || null });
    this._emit('xp-answer', { question, ...res });
    return res;
  }

  /* ================= AR ================= */
  async ar() {
    const r = await launchAR(this.product, this.model?.root);
    this.analytics.track('ar', { product: this.product.id, result: r });
    if (r === 'desktop') this._toast('Ouvrez cette page sur votre mobile pour voir le produit chez vous.');
    if (r === 'unavailable') this._toast('Réalité augmentée indisponible pour ce produit.');
  }

  /* ================= mode borne ================= */
  _kiosk() {
    if (this._kioskOn || !this.cfg || this.cfg.kiosk?.allowed === false) return; this._kioskOn = true; // option « Mode borne »
    const idle = (this.cfg?.kiosk?.idleSeconds || 45) * 1000;
    const arm = () => { clearTimeout(this._idleT); this._idleT = setTimeout(() => { this._exitCompare(); this.view('home'); this.playTour({ loop: true }); }, idle); };
    ['pointerdown', 'wheel', 'keydown'].forEach(t => this.addEventListener(t, arm));
    arm();
  }
  _userInteract() { if (this._tourId) this.stopTour(); this._hideCard(); this.analytics?.track('interact', { product: this.product?.id }); this.ui.hint.style.opacity = 0; }

  /* ================= outils pour le studio ================= */
  /** Point du modèle sous le curseur (coordonnées locales au modèle) ou null. */
  pick(clientX, clientY) {
    if (!this.model) return null;
    const r = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    const ray = new THREE.Raycaster(); ray.setFromCamera(ndc, this.camera);
    const hit = ray.intersectObject(this.model.root, true).find(h => h.object.visible && h.face);
    if (!hit) return null;
    const p = this.model.root.worldToLocal(hit.point.clone());
    const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
    const inv = new THREE.Matrix4().copy(this.model.root.matrixWorld).invert();
    n.transformDirection(inv);
    const r3 = v => +v.toFixed(4);
    return { position: [p.x, p.y, p.z].map(r3), normal: [n.x, n.y, n.z].map(r3), object: hit.object.name, material: [].concat(hit.object.material)[0]?.name || '' };
  }
  /** Inventaire du modèle courant : ancres, matières, animations, variantes intégrées. */
  inspect() {
    const m = this.model; if (!m) return null;
    const mats = new Set(); m.root.traverse(o => o.isMesh && [].concat(o.material).forEach(x => x.name && mats.add(x.name)));
    return { anchors: [...m.anchors.keys()], materials: [...mats], animations: (m.animations || []).map(a => a.name), variants: m.variantNames || [], canFold: m.canFold, size: m.size };
  }
  /** Position actuelle de la caméra (pour définir la vue d'accueil). */
  cameraState() { const r = v => +v.toFixed(3); return { position: this.camera.position.toArray().map(r), target: this.controls.target.toArray().map(r) }; }
  /** Ajoute un repère visuel temporaire (studio). */
  marker(position) {
    if (!this._marker) { this._marker = new THREE.Mesh(new THREE.SphereGeometry(.012, 16, 16), new THREE.MeshBasicMaterial({ color: 0x0b57d0, depthTest: false })); this._marker.renderOrder = 10; }
    if (!position || !this.model) { this._marker.removeFromParent(); return; }
    this.model.root.add(this._marker); this._marker.position.set(...position);
  }

  /* ================= UI ================= */
  _bindUI() {
    this.ui.tb.addEventListener('click', e => {
      const a = e.target.closest('button')?.dataset.a; if (!a) return;
      ({
        left: () => this.orbit(-Math.PI / 4, 0), right: () => this.orbit(Math.PI / 4, 0),
        up: () => this.orbit(0, -.35), down: () => this.orbit(0, .35),
        in: () => this.zoom(.78), out: () => this.zoom(1.28),
        fold: () => this.setFold(this.foldTarget > .5 ? 0 : 1),
        tour: () => this._tourId ? this.stopTour() : this.playTour(),
        compare: () => this.compareSet ? this._exitCompare() : this.compare(),
        ar: () => this.ar(),
        reset: () => { this._exitCompare(); this.view('home'); this.setAutoRotate(true); },
        full: () => document.fullscreenElement ? document.exitFullscreen() : this.requestFullscreen?.()
      })[a]?.();
    });
    this.ui.ask.querySelector('form').addEventListener('submit', e => { e.preventDefault(); const i = this.ui.ask.querySelector('input'); this.ask(i.value); i.value = ''; });
  }

  _loop() {
    if (!this.running) return;
    requestAnimationFrame(() => this._loop());
    const dt = Math.min(.05, this.clock.getDelta()), t = this.clock.elapsedTime;
    if (this.tw) {
      const k = Math.min(1, (performance.now() - this.tw.s) / this.tw.dur), e = ease(k);
      this.camera.position.lerpVectors(this.tw.p0, this.tw.p1, e); this.controls.target.lerpVectors(this.tw.t0, this.tw.t1, e);
      if (k >= 1) this.tw = null;
    }
    if (this.colorJobs.length) this.colorJobs = this.colorJobs.filter(j => { j.m[j.k].lerp(j.c, .12); return j.m[j.k].getHex() !== j.c.getHex() && Math.abs(j.m[j.k].r - j.c.r) + Math.abs(j.m[j.k].g - j.c.g) + Math.abs(j.m[j.k].b - j.c.b) > .002; });
    const m = this.model;
    if (m && !this.compareSet) {
      if (m.effects.hilight) {
        if (this.effects.hilight) { const i = Math.floor(t * 1.5) % 4, f = (t * 1.5) % 1; m.effects.hilight.color.copy(HILIGHT[i]).lerp(HILIGHT[(i + 1) % 4], f).multiplyScalar(1.2 + .5 * Math.sin(t * 5)); }
        else m.effects.hilight.color.set(0x1b1b1e);
      }
      if (m.chargeRing) { const on = !!this.effects.charge; m.effects.charge.opacity += ((on ? .55 + .35 * Math.sin(t * 4) : 0) - m.effects.charge.opacity) * .15; m.chargeRing.scale.setScalar(1 + (on ? .04 * Math.sin(t * 4) : 0)); }
      (m.screens || [m.screen]).forEach(s => s?.tick(dt));
      if (m.canFold && Math.abs(this.foldT - this.foldTarget) > .001) { this.foldT += (this.foldTarget - this.foldT) * .1; m.setFold(this.foldT); this._placeShadow(); }
    }
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    // projection des points d'intérêt / étiquettes
    const w = this.clientWidth, h = this.clientHeight;
    if (!this.compareSet) for (const hp of this.hotspots) {
      hp.obj.getWorldPosition(_v); hp.obj.getWorldQuaternion(_q); _n.copy(hp.obj.userData.n).applyQuaternion(_q);
      _c.copy(this.camera.position).sub(_v).normalize();
      const vis = this._showHS && _n.dot(_c) > .2;
      _v.project(this.camera);
      hp.el.style.transform = `translate(${(_v.x * .5 + .5) * w}px,${(-_v.y * .5 + .5) * h}px)`;
      hp.el.classList.toggle('off', !vis);
    } else this.compareSet.forEach(({ m: cm }, i) => {
      const el = this.ui.labels.children[i]; if (!el) return;
      _v.set(0, -cm.size.H / 2 - .12, 0); cm.root.localToWorld(_v); _v.project(this.camera);
      el.style.left = (_v.x * .5 + .5) * w + 'px'; el.style.top = (-_v.y * .5 + .5) * h + 'px';
    });
  }
}

if (!customElements.get('xp-viewer')) customElements.define('xp-viewer', XPViewer);
